import {
  PROJECT_DEVICES_FETCH_STARTED,
  PROJECT_DEVICES_FETCH_SUCCEEDED,
  PROJECT_DEVICES_FETCH_FAILED,
  PROJECT_DEVICES_CHANGE_PAGE,
  PROJECT_DEVICES_CHANGE_ORDER_AND_ORDER_BY,
  PROJECT_DEVICES_SET_FILTER,
  PROJECT_DEVICES_SET_DEVICE_TYPE,
  OPEN_PROJECT_DEVICES_POPUP,
  PROJECT_DEVICES_POPUP_SET_ADD_MODE,
  PROJECT_DEVICES_SET_FORM_DEVICES,
  PROJECT_DEVICES_POPUP_SET_REMOVED_DEVICES,
  PROJECT_DEVICES_POPUP_SET_ADDED_DEVICES,
  PROJECT_DEVICES_POPUP_SET_STATE_CHANGED,
  PROJECT_DEVICES_POPUP_SET_STATE_DIFF,
  PROJECT_DEVICES_POPUP_PROJECT_FETCH_STARTED,
  PROJECT_DEVICES_POPUP_PROJECT_FETCH_SUCCEEDED,
  PROJECT_DEVICES_POPUP_PROJECT_FETCH_FAILED,
  PROJECT_DEVICES_CLEAR,
  PROJECT_DEVICES_POPUP_TOGGLE_DEVICE_ALERT,
  PROJECT_DEVICES_POPUP_SET_DEVICE_ALERT,
  PROJECT_DEVICES_POPUP_TOGGLE_ALL_DEVICES_ALERT
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError, fetchJson } from '#action/fetch';
import { getRequestOptions, getSearchParamsWithoutEmptyFields } from '#helper';

let abortController = new AbortController();
// Separate controller: fetchProject runs concurrently with fetchProjectDevices,
// so they must not cancel each other.
let projectAbortController = new AbortController();

export const fetchProjectDevices = (projectId, exclude, editForm) =>
  async (dispatch, getState) => {
    try {
      const {
        page,
        itemsPerPage,
        order,
        orderBy,
        filter,
        filterBy,
        deviceType,
        addMode,
        formDevices,
        removedDevices,
        newlyAddedDevices
      } = getState().projectDevicesPopup;
      let projects = [];
      // The devices the project form holds are mirrored into this slice when the
      // popup opens and on every add/remove. In create mode all of them were
      // added during this session.
      const currentFormDevices = formDevices ?? [];
      const addedToFormDevices = currentFormDevices;

      abortController.abort();
      abortController = new AbortController();
      const { signal } = abortController;

      dispatch({
        type: PROJECT_DEVICES_FETCH_STARTED
      });

      const limit = itemsPerPage;
      const offset = (page - 1) * itemsPerPage;
      const searchParams = new URLSearchParams({
        order,
        orderBy,
        offset,
        limit,
        pagination: 1,
        projects: projectId,
        withoutYoungest: 1
      });

      if (projectId) {
        const projectsRes = await fetch(webPortalApi.buildApiUrl(`project?ids=${projectId}`), getRequestOptions());
        projects = await projectsRes.json();
      }

      const openedFromCreateForm = editForm && !projectId;

      // // Opened from create form
      if (openedFromCreateForm) {
        searchParams.delete('projects');
        const devicesIds = formDevices?.map(d => d.id).join(',');

        if (!addMode) {
          if (!currentFormDevices.length) {
            dispatch({
              type: PROJECT_DEVICES_FETCH_SUCCEEDED,
              payload: {
                devices: []
              }
            });
            return;
          }
          searchParams.set('ids', devicesIds);
        } else {
          const addedDevicesIds = addedToFormDevices?.map(d => d.id).join(',');
          searchParams.set('excludeIds', addedDevicesIds);
        }
      }

      // Opened from edit form
      if (editForm && projectId) {
        let removedDevicesId = [];
        let addedDevicesId = [];

        if (deviceType && deviceType.value !== 'All types') {
          removedDevicesId = removedDevices
            .filter(d => d.type === deviceType.value)
            .map(d => d.id)
            .join(',');

          addedDevicesId = newlyAddedDevices
            .filter(d => d.type === deviceType.value)
            .map(d => d.id)
            .join(',');
        } else {
          removedDevicesId = removedDevices.map(d => d.id).join(',');
          addedDevicesId = newlyAddedDevices.map(d => d.id).join(',');
        }

        if (!addMode) {
          if (!currentFormDevices.length) {
            dispatch({
              type: PROJECT_DEVICES_FETCH_SUCCEEDED,
              payload: {
                devices: []
              }
            });
            return;
          }
          if (removedDevicesId.length) {
            searchParams.set('excludeIds', removedDevicesId);
          }
          if (addedDevicesId.length) {
            searchParams.set('ids', addedDevicesId);
          }
        } else {
          searchParams.set('excludeProjects', projectId);

          if (removedDevicesId.length) {
            const currentProject = projects.find(p => p.id === projectId);
            searchParams.set('projects', currentProject.parentId);
            searchParams.set('ids', removedDevicesId);
          }
          if (addedDevicesId.length) {
            searchParams.set('excludeIds', addedDevicesId);
          }
        }
      }

      if (deviceType) {
        const allTypeSelected = deviceType.value === 'All types';

        if (allTypeSelected) {
          searchParams.delete('types');
        } else {
          searchParams.set('types', deviceType.value);
        }

        if (openedFromCreateForm && searchParams.has('ids') && !allTypeSelected) {
          searchParams.set('combineFilters', 1);
        } else {
          searchParams.delete('combineFilters');
        }
      }

      if (filter && typeof filter === 'string') {
        searchParams.set('filterBy', filterBy);
        searchParams.set('filter', filter);
      }

      if ((exclude || addMode) && !openedFromCreateForm) {
        searchParams.delete('projects');

        const removedDevicesId = removedDevices.map(d => d.id).join(',');
        const addedDevicesId = newlyAddedDevices.map(d => d.id).join(',');

        searchParams.set('excludeProjects', projectId);

        if (removedDevicesId.length) {
          const currentProject = projects.find(p => p.id === projectId);
          searchParams.set('projects', currentProject.parentId);
          searchParams.set('ids', removedDevicesId);
        }
        if (addedDevicesId.length) {
          searchParams.set('excludeIds', addedDevicesId);
        }
      }

      // Debounce to do not send multiple requests in short time
      await new Promise(res => {
        setTimeout(() => {
          res();
        }, 200);
      });

      const paramsWithoutEmptyFields = getSearchParamsWithoutEmptyFields(searchParams);
      const url = webPortalApi.buildApiUrl(`device/distinct/details?${paramsWithoutEmptyFields}`);
      const res = await fetch(url, { ...getRequestOptions(), signal });

      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError('Response is not json');
      }
      if (!res.ok) {
        dispatch(
          snackBarError({
            message: 'Error in request'
          })
        );
      }

      const { devices, last } = await res.json();

      const actionPayload = {
        type: PROJECT_DEVICES_FETCH_SUCCEEDED,
        payload: {
          devices,
          projects,
          exclude,
          withDisabledBy: true
        },
        totalCount: last
      };

      dispatch(actionPayload);
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }
      dispatch({
        type: PROJECT_DEVICES_FETCH_FAILED,
        error: e
      });
    }
  };

export const fetchProject = id => async dispatch => {
  try {
    projectAbortController.abort();
    projectAbortController = new AbortController();
    const { signal } = projectAbortController;

    dispatch({
      type: PROJECT_DEVICES_POPUP_PROJECT_FETCH_STARTED
    });

    // Debounce to do not send multiple requests in short time
    await new Promise(res => {
      setTimeout(() => {
        res();
      }, 200);
    });

    const url = webPortalApi.buildApiUrl(`project?ids=${id}&belongings=1`);
    const res = await fetch(url, { ...getRequestOptions(), signal });

    const contentType = res.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request'
        })
      );
    }

    const project = await res.json();

    dispatch({
      type: PROJECT_DEVICES_POPUP_PROJECT_FETCH_SUCCEEDED,
      payload: project[0]
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return;
    }
    dispatch({
      type: PROJECT_DEVICES_POPUP_PROJECT_FETCH_FAILED,
      error: e
    });
  }
};

export const fetchFormDevices = async devices => {
  const searchParams = new URLSearchParams({
    order: 'desc',
    orderBy: 'title',
    ids: devices,
    offset: 0,
    limit: 10,
    pagination: 1,
    withoutYoungest: 1
  });
  const url = webPortalApi.buildApiUrl(`device/distinct/details?${searchParams.toString()}`);
  const result = await fetchJson(url);
  return result.devices;
};

export const setDevices = (devices = [], data = {}) => (dispatch, getState) => {
  try {
    const { formDevices } = getState().projectDevicesPopup;
    const { alertId } = data;
    const payload = {
      devices,
      formDevices,
      alertId
    };

    dispatch({
      type: PROJECT_DEVICES_FETCH_SUCCEEDED,
      payload,
      totalCount: devices?.length
    });
  } catch (e) {
    console.warn(e);
  }
};

export const setSelectedDeviceWithAlert = selectedDevices => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_POPUP_SET_DEVICE_ALERT,
    payload: selectedDevices
  });
};

export const toggleDeviceAlert = selectedDevice => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_POPUP_TOGGLE_DEVICE_ALERT,
    payload: {
      device: selectedDevice
    }
  });
};

export const toggleAllDevicesAlert = toggleState => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_POPUP_TOGGLE_ALL_DEVICES_ALERT,
    payload: { toggleState }
  });
};

export const setRemovedDevices = devices => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_POPUP_SET_REMOVED_DEVICES,
    payload: devices
  });
};

export const setAddedDevices = devices => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_POPUP_SET_ADDED_DEVICES,
    payload: devices
  });
};

export const setFormData = formDevices => dispatch => {
  try {
    const payload = {
      formDevices
    };

    dispatch({
      type: PROJECT_DEVICES_SET_FORM_DEVICES,
      payload,
      totalCount: formDevices?.length
    });
  } catch (e) {
    console.warn(e);
  }
};

export const changeOrderAndOrderBy = (
  newOrder,
  newOrderBy,
  projectId,
  addMode,
  editForm
) => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_CHANGE_ORDER_AND_ORDER_BY,
    payload: { order: newOrder, orderBy: newOrderBy }
  });
  dispatch(fetchProjectDevices(projectId, addMode, editForm));
};

export const changePage = (
  newPage,
  projectId,
  addMode,
  editForm,
  refetch = true
) => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_CHANGE_PAGE,
    payload: newPage
  });

  if (refetch) {
    dispatch(fetchProjectDevices(projectId, addMode, editForm));
  }
};

export const setFilter = (newFilter, projectId, addMode, editForm) => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_SET_FILTER,
    payload: newFilter
  });

  dispatch(fetchProjectDevices(projectId, addMode, editForm));
};

export const setDeviceType = (newType, projectId, addMode, openedFromCreateForm) => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_SET_DEVICE_TYPE,
    payload: newType
  });

  dispatch({
    type: PROJECT_DEVICES_CHANGE_PAGE,
    payload: 1
  });

  dispatch(fetchProjectDevices(projectId, addMode, openedFromCreateForm));
};

export const setAddMode = isEnabled => (dispatch, getState) => {
  const { page } = getState().projectDevicesPopup;

  if (page !== 1) {
    dispatch({
      type: PROJECT_DEVICES_CHANGE_PAGE,
      payload: 1
    });
  }

  dispatch({
    type: PROJECT_DEVICES_POPUP_SET_ADD_MODE,
    payload: isEnabled
  });
};

export const setOpenPopup = (isOpen, member, addMode) => dispatch => {
  if (addMode) {
    dispatch(setAddMode(addMode));
  }

  if (member) {
    dispatch({
      type: OPEN_PROJECT_DEVICES_POPUP,
      payload: {
        [member]: isOpen
      }
    });
  } else {
    dispatch({
      type: OPEN_PROJECT_DEVICES_POPUP,
      payload: isOpen
    });
  }
};

export const setPopupStateChanged = isChanged => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_POPUP_SET_STATE_CHANGED,
    payload: isChanged
  });
};

export const setPopupStateDiff = diff => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_POPUP_SET_STATE_DIFF,
    payload: diff
  });
};

export const clear = () => dispatch => {
  abortController.abort();
  projectAbortController.abort();
  dispatch({
    type: PROJECT_DEVICES_CLEAR
  });
};
